// Static Data - No Backend Required
// All data is embedded for static site generation

export const companyInfo = {
  name: "Ecovera Sourcing LLP",
  tagline: "Your Supply, Our Priority",
  email: "info@ecoverasourcing.com",
  phone: "+91 9057905733",
  phoneLink: "+919057905733",
  address: {
    street: "C30, C Block, Sector 63",
    city: "Noida",
    state: "Uttar Pradesh",
    pincode: "201301",
    country: "India",
    full: "C30, C Block, Sector 63, Noida 201301"
  },
  businessHours: {
    weekdays: "Monday - Friday: 9:00 AM - 6:00 PM",
    saturday: "Saturday: 10:00 AM - 2:00 PM",
    sunday: "Sunday: Closed"
  },
  social: {
    linkedin: "https://linkedin.com/company/ecoverasourcing",
    twitter: "https://twitter.com/ecoverasourcing"
  }
};

export const stats = {
  suppliers: 500,
  products_delivered: 10000,
  clients_served: 200,
  countries: 15,
  years_experience: 10,
  quality_rate: 99.5,
  satisfaction_rate: 98,
  product_categories: 50
};

export const services = [
  {
    id: 1,
    title: 'Product Sourcing & Procurement',
    shortDesc: 'End-to-end sourcing support from supplier identification to final delivery.',
    description: 'We handle the complete sourcing process, from identifying the right suppliers to managing procurement and ensuring timely delivery. Our extensive network allows us to find the best manufacturers for your specific requirements.',
    features: ['Supplier identification', 'Price negotiation', 'Sample coordination', 'Order management'],
    icon: 'Package'
  },
  {
    id: 2,
    title: 'Supplier Discovery & Vendor Management',
    shortDesc: 'Connecting you with verified, reliable, and high-performing manufacturers.',
    description: 'Our rigorous vetting process ensures you work with only the most reliable and capable manufacturers across India. We manage relationships and track performance to maintain quality standards.',
    features: ['Factory audits', 'Supplier verification', 'Performance tracking', 'Relationship management'],
    icon: 'Search'
  },
  {
    id: 3,
    title: 'Customized Product Development',
    shortDesc: 'Transforming concepts into market-ready products with tailored design solutions.',
    description: 'From initial concept to final production, we support your product development journey with design expertise, prototyping, and manufacturing setup.',
    features: ['Concept refinement', 'Design support', 'Prototype development', 'Production setup'],
    icon: 'Lightbulb'
  },
  {
    id: 4,
    title: 'Quality Control & Inspection',
    shortDesc: 'Ensuring product consistency, compliance, and excellence at every stage of production.',
    description: 'Our comprehensive 5-stage QC process guarantees that every product meets your specifications and quality standards before shipping.',
    features: ['Pre-production inspection', 'In-process monitoring', 'Pre-shipment inspection', 'Quality reporting'],
    icon: 'Shield'
  },
  {
    id: 5,
    title: 'Production Monitoring',
    shortDesc: 'Real-time oversight to keep manufacturing on track and aligned with specifications.',
    description: 'We monitor production progress in real-time, ensuring timelines are met and any issues are addressed immediately to prevent delays.',
    features: ['Timeline tracking', 'Specification compliance', 'Issue resolution', 'Progress reporting'],
    icon: 'FileCheck'
  },
  {
    id: 6,
    title: 'Supply Chain Management',
    shortDesc: 'Seamless handling of timelines, communication, logistics, and documentation.',
    description: 'End-to-end supply chain coordination including logistics, customs documentation, and delivery management for hassle-free operations.',
    features: ['Logistics coordination', 'Documentation', 'Customs support', 'Delivery tracking'],
    icon: 'Truck'
  },
  {
    id: 7,
    title: 'Trend Research & Product Strategy',
    shortDesc: 'Insights and guidance to build relevant, competitive product lines.',
    description: 'Stay ahead of market trends with our research and strategic guidance to develop products that resonate with your target audience.',
    features: ['Market analysis', 'Trend forecasting', 'Competitive insights', 'Product roadmapping'],
    icon: 'TrendingUp'
  },
  {
    id: 8,
    title: 'Trading & Consolidation',
    shortDesc: 'Supporting faster procurement through curated and ready-to-ship product options.',
    description: 'Access our curated inventory of quality products ready for immediate shipping, with consolidation services for efficient multi-supplier orders.',
    features: ['Ready inventory', 'Multi-supplier consolidation', 'Bulk purchasing', 'Fast shipping'],
    icon: 'Globe'
  },
  {
    id: 9,
    title: 'Ethical & Transparent Operations',
    shortDesc: 'Ensuring responsible sourcing, clear communication, and on-time execution.',
    description: 'We operate with complete transparency and ethical standards, ensuring responsible sourcing practices and clear communication throughout.',
    features: ['Ethical sourcing', 'Clear communication', 'On-time delivery', 'Full transparency'],
    icon: 'Handshake'
  }
];

export const processSteps = [
  {
    step: '01',
    title: 'Discovery & Consultation',
    description: 'We begin by understanding your unique requirements, product specifications, and business goals through detailed consultation.'
  },
  {
    step: '02',
    title: 'Supplier Identification',
    description: 'Leveraging our extensive network, we identify and vet the most suitable manufacturers and suppliers for your needs.'
  },
  {
    step: '03',
    title: 'Product Development',
    description: 'Our team works closely with manufacturers to develop prototypes, refine designs, and ensure product specifications are met.'
  },
  {
    step: '04',
    title: 'Quality Assurance',
    description: 'Rigorous quality control checks at every production stage to ensure consistency, compliance, and excellence.'
  },
  {
    step: '05',
    title: 'Production & Monitoring',
    description: 'Real-time oversight of manufacturing processes with regular updates and coordination to keep everything on track.'
  },
  {
    step: '06',
    title: 'Delivery & Support',
    description: 'Seamless logistics management from factory to your doorstep, with continued support for future requirements.'
  }
];

export const caseStudies = [
  {
    id: 1,
    title: 'Home Decor Brand Launch',
    category: 'Product Development',
    client: 'Nordic Living Co.',
    image: 'https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?w=600&q=80',
    challenge: 'A new home decor brand needed to source and develop 50+ SKUs from concept to market launch within 6 months.',
    solution: 'We identified specialized manufacturers across India, managed custom product development, conducted rigorous QC, and coordinated production timelines.',
    results: ['Launched 52 products on schedule', '98% quality approval rate', '20% cost savings vs. initial quotes', 'Established reliable supplier relationships'],
    timeline: '6 months'
  },
  {
    id: 2,
    title: 'Electronics Accessories Scaling',
    category: 'Supply Chain Management',
    client: 'TechGear Solutions',
    image: 'https://images.unsplash.com/photo-1588508065123-287b28e013da?w=600&q=80',
    challenge: 'Rapidly scale production of mobile accessories from 10,000 to 100,000 units monthly while maintaining quality.',
    solution: 'Expanded supplier network, implemented multi-tier QC process, and established backup manufacturing partnerships.',
    results: ['10x production increase achieved', '99.2% quality compliance', '15% reduction in unit costs', 'Zero stockouts during scaling'],
    timeline: '4 months'
  },
  {
    id: 3,
    title: 'Sustainable Fashion Line',
    category: 'Ethical Sourcing',
    client: 'EcoWear Fashion',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80',
    challenge: 'Launch an eco-friendly fashion line with certified sustainable materials and ethical manufacturing.',
    solution: 'Sourced GOTS-certified organic cotton suppliers, established fair-trade manufacturing partnerships, and implemented full supply chain transparency.',
    results: ['100% sustainable materials sourced', 'Fair-trade certified production', '30% premium pricing achieved', 'Full traceability implemented'],
    timeline: '8 months'
  }
];

export const testimonials = [
  {
    id: 1,
    content: "Working with this team transformed our sourcing operations. Their attention to detail and supplier network helped us launch three new product lines ahead of schedule.",
    name: "Sarah Johnson",
    role: "Procurement Director",
    company: "Lifestyle Brands Co.",
    initials: "SJ",
    rating: 5
  },
  {
    id: 2,
    content: "Exceptional service from concept to delivery. The quality control process gave us complete confidence, and the products exceeded our expectations.",
    name: "Raj Patel",
    role: "CEO",
    company: "TechHome Solutions",
    initials: "RP",
    rating: 5
  },
  {
    id: 3,
    content: "Their transparent approach and ethical sourcing practices align perfectly with our values. Communication is seamless, and delivery is always on time.",
    name: "Michael Chen",
    role: "Supply Chain Manager",
    company: "Retail Innovations Ltd.",
    initials: "MC",
    rating: 5
  },
  {
    id: 4,
    content: "The customized product development service is outstanding. They turned our concepts into market-ready products with incredible precision and speed.",
    name: "Priya Sharma",
    role: "Product Development Head",
    company: "Fashion Forward Inc.",
    initials: "PS",
    rating: 5
  }
];

export const faqs = [
  {
    id: 1,
    question: "What industries do you serve?",
    answer: "We serve a wide range of industries including consumer goods, electronics, textiles, home furnishings, automotive parts, packaging, and more. Our flexible approach allows us to adapt to various sector-specific requirements.",
    category: "General"
  },
  {
    id: 2,
    question: "How do you ensure product quality?",
    answer: "We implement a rigorous 5-stage quality control process: pre-production inspection, in-process monitoring, pre-shipment inspection, loading supervision, and post-delivery audit. Each stage is documented and reported to our clients.",
    category: "Quality"
  },
  {
    id: 3,
    question: "What is your minimum order quantity (MOQ)?",
    answer: "MOQ varies by product category and supplier. However, we work with manufacturers who accommodate both small trial orders and large-scale production runs. Contact us with your requirements for specific MOQ information.",
    category: "Orders"
  },
  {
    id: 4,
    question: "How long does the sourcing process take?",
    answer: "Typical sourcing timelines range from 2-4 weeks for supplier identification and 4-8 weeks for sample development. Production lead times vary by product complexity, usually 30-60 days after order confirmation.",
    category: "Process"
  },
  {
    id: 5,
    question: "Do you handle customs and logistics?",
    answer: "Yes, we provide end-to-end supply chain management including documentation, customs clearance coordination, freight forwarding, and last-mile delivery arrangements. We work with trusted logistics partners globally.",
    category: "Logistics"
  },
  {
    id: 6,
    question: "Can you help with product design and development?",
    answer: "Absolutely! Our product development team can assist with concept refinement, technical specifications, material selection, prototype development, and design-for-manufacturing optimization.",
    category: "Services"
  },
  {
    id: 7,
    question: "What makes Ecovera different from other sourcing companies?",
    answer: "Our strength lies in our extensive vetted supplier network across India, our rigorous quality control processes, transparent operations, and our commitment to ethical sourcing. We combine strategic expertise with hands-on execution to deliver results that exceed expectations.",
    category: "General"
  },
  {
    id: 8,
    question: "Do you offer sample development services?",
    answer: "Yes, we coordinate sample development with manufacturers and manage the entire process from initial concept to approved samples. We ensure samples meet your specifications before proceeding to bulk production.",
    category: "Services"
  }
];

export const whyChooseUs = [
  {
    title: 'Extensive Network',
    description: 'Access to vetted manufacturers and suppliers across India in diverse product categories.',
    icon: 'Globe'
  },
  {
    title: 'Quality First',
    description: 'Rigorous QC processes ensure consistent excellence and compliance at every production stage.',
    icon: 'Shield'
  },
  {
    title: 'Transparent Operations',
    description: 'Clear communication, real-time updates, and complete visibility throughout the entire process.',
    icon: 'CheckCircle'
  },
  {
    title: 'Ethical Sourcing',
    description: 'Committed to responsible sourcing practices and sustainable supplier partnerships.',
    icon: 'Handshake'
  },
  {
    title: 'On-Time Delivery',
    description: 'Proven track record of meeting timelines and maintaining reliable, consistent availability.',
    icon: 'Clock'
  },
  {
    title: 'Custom Solutions',
    description: 'Tailored approach to meet unique requirements and transform concepts into reality.',
    icon: 'Lightbulb'
  }
];

export default {
  companyInfo,
  stats,
  services,
  processSteps,
  caseStudies,
  testimonials,
  faqs,
  whyChooseUs
};
